import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../../utils/units';
import { Check, Clock, Calendar, Info, X } from 'lucide-react';
import type { PremiumService } from '../../types/customization';

interface ServiceCardProps {
  service: PremiumService;
  isSelected: boolean;
  onToggle: () => void;
  currency: string;
  language: string;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  isSelected,
  onToggle,
  currency,
  language
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const Icon = service.icon;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`relative rounded-xl border-2 overflow-hidden transition-all duration-200 ${
        isSelected
          ? 'border-gold bg-gold/5 shadow-md'
          : 'border-gold/20 hover:border-gold/40 bg-white'
      }`}
    >
      {/* Service Card Header */}
      <div 
        className="p-5 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${
            isSelected ? 'bg-gold text-white' : 'bg-gold/10 text-gold'
          }`}>
            <Icon className="w-6 h-6" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gold-dark">{service.name}</h4>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(!showDetails);
                }}
                className="text-gold/60 hover:text-gold p-1 rounded-full hover:bg-gold/5 transition-colors"
              >
                {showDetails ? <X className="w-4 h-4" /> : <Info className="w-4 h-4" />}
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-gold">
                {formatCurrency(service.price, currency, language)}
              </p>
              
              {service.duration && (
                <span className="text-sm text-black/60 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {service.duration}
                </span>
              )}
            </div>
            
            <p className="text-sm text-black/70 mt-2 line-clamp-2">
              {service.description}
            </p>
          </div>
          
          {isSelected && (
            <div className="absolute top-4 right-4 w-6 h-6 bg-gold rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Service Details Panel */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gold/10 bg-white/50"
          >
            <div className="p-5 space-y-4">
              {service.features && service.features.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-gold-dark mb-2">Includes</h5>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-black/70">
                        <Check className="w-4 h-4 text-gold flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm">
                {service.duration && (
                  <div className="flex items-center gap-2 text-black/70">
                    <Calendar className="w-4 h-4 text-gold" />
                    <span>Duration: {service.duration}</span>
                  </div>
                )}
                
                {service.availability && (
                  <div className="flex items-center gap-2 text-black/70">
                    <Clock className="w-4 h-4 text-gold" />
                    <span>Availability: {service.availability}</span>
                  </div>
                )}
                
                {service.guarantee && (
                  <div className="flex items-center gap-2 text-black/70">
                    <Check className="w-4 h-4 text-gold" />
                    <span>Guarantee: {service.guarantee}</span>
                  </div>
                )}
              </div>
              
              {service.savings && (
                <div className="mt-2 p-3 bg-green-50 rounded-lg text-sm text-green-800">
                  <span className="font-medium">Savings: </span>
                  {service.savings}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};